'use client';

import { useTranslations } from 'next-intl';

import { SubscribeAlertButton } from '@/components/SubscribeAlertButton';
import { useSubscribeEntityAlert } from '@/lib/alerts/useSubscribeEntityAlert';

export interface UniversActionSlotProps {
  /** Ids of every sous-univers belonging to the parent univers (snapshot). */
  sousUniversIds: number[];
  universName: string;
  /**
   * Localized name of each sous-univers, keyed by id, so the advanced-alert
   * chips show the distinct sous-univers names rather than the parent name.
   */
  sousUniversNamesById: Record<number, string>;
}

/**
 * UniversActionSlot
 *
 * "Follow this universe" button for a parent univers page. Subscribing to the
 * parent univers means subscribing to a snapshot of all of its sous-univers, so
 * the modal is opened with the full list of sous-univers ids (multi-id) via the
 * shared `useSubscribeEntityAlert` hook. Per-id names are forwarded so the
 * advanced-alert chips render each sous-univers' own name.
 *
 * Renders nothing when the univers has no sous-univers (C5): there is nothing
 * to subscribe to, and the server action rejects empty id arrays.
 */
export const UniversActionSlot = ({
  sousUniversIds,
  universName,
  sousUniversNamesById,
}: UniversActionSlotProps) => {
  const t = useTranslations('Blocs.BoutonSuivreAlerte');
  const { subscribe } = useSubscribeEntityAlert({
    entityType: 'sous_univers',
    entityIds: sousUniversIds,
    name: universName,
    entityNamesById: sousUniversNamesById,
  });

  if (sousUniversIds.length === 0) {
    return null;
  }

  return (
    <SubscribeAlertButton label={t('suivre_cet_univers')} onClick={subscribe} />
  );
};
